import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { LoadingState, ErrorState, EmptyState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Tv, Users, Plug, Film, ArrowRight, ShieldCheck, Languages } from "lucide-react";

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5 hover:border-slate-700/80 transition-colors" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">{label}</span>
        <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center">
          <Icon className="w-4 h-4 text-violet-300" />
        </div>
      </div>
      <div className="text-3xl font-display font-extrabold text-slate-50">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function Overview() {
  const { selectedChannel } = useStudio();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const res = await api.get("/overview");
      setData(res.data);
    } catch (e) { setError(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <LoadingState label="Loading overview…" />;

  const ch = selectedChannel?.data;
  const health = data.integration_health;

  return (
    <div className="animate-in-up">
      <div className="mb-8">
        <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-1">Foundation</p>
        <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">Overview</h1>
        <p className="text-sm text-slate-400 mt-2">A live snapshot of your workspace.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-8">
        <Kpi icon={Tv} label="Channels" value={data.channel_count} sub={`${data.sample_channel_count} sample`} testid="kpi-channels" />
        <Kpi icon={Users} label="AI Team roles" value={data.roles_count} sub="configured" testid="kpi-roles" />
        <Kpi icon={Plug} label="Integrations" value={`${health.connected}/${health.total}`} sub={`core connected${health.extra_connected ? ` · +${health.extra_connected} more` : ""}`} testid="kpi-integrations" />
        <Kpi icon={Film} label="Render jobs" value={data.render_jobs_count} sub="Phase 0 engine" testid="kpi-renders" />
      </div>

      <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-6 mb-5" data-testid="channels-by-language">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-display font-semibold text-slate-100 flex items-center gap-2"><Languages className="w-4 h-4 text-violet-300" /> Channels by language</h3>
          <Button variant="outline" size="sm" onClick={() => navigate("/channels")} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="overview-open-channels">All channels <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></Button>
        </div>
        {(!data.channels_by_language || data.channels_by_language.length === 0) ? (
          <p className="text-sm text-slate-500">No channels yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.channels_by_language.map((g) => (
              <div key={g.language} className="bg-slate-900/50 border border-slate-800 rounded-lg p-4" data-testid={`lang-group-${g.language}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-200">{g.language}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-300 border border-violet-500/30" data-testid={`lang-count-${g.language}`}>{g.count} channel{g.count === 1 ? "" : "s"}</span>
                </div>
                <ul className="space-y-1">
                  {g.channels.map((c) => (
                    <li key={c.id} onClick={() => navigate(`/channels/${c.id}`)}
                      className="text-xs text-slate-400 flex items-center justify-between gap-2 cursor-pointer hover:text-slate-200">
                      <span className="truncate">{c.name}</span>
                      <span className="text-slate-600 truncate max-w-[45%]">{c.niche || "—"}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-[#111827] border border-slate-800/80 rounded-xl p-6" data-testid="active-channel-card">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-display font-semibold text-slate-100">Active channel</h3>
            {selectedChannel && (
              <Button variant="outline" size="sm" onClick={() => navigate(`/channels/${selectedChannel.id}`)}
                className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="overview-open-channel">
                Open profile <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            )}
          </div>
          {!ch ? (
            <EmptyState title="No channel selected" description="Create a channel profile to see its details here."
              action={<Button onClick={() => navigate("/channels/new")} className="bg-violet-600 hover:bg-violet-500">Create channel</Button>} />
          ) : (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xl font-display font-bold text-slate-50">{ch.name}</span>
                {selectedChannel.is_sample && <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-300 border border-violet-500/30">Sample</span>}
                <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">v{selectedChannel.version}</span>
              </div>
              <p className="text-sm text-slate-400 mb-5">{ch.description || "No description yet."}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <Detail icon={Languages} label="Language" value={ch.primary_language} />
                <Detail label="Niche" value={ch.niche || "—"} />
                <Detail label="Format" value={`${ch.default_format} · ${ch.aspect_ratio}`} />
                <Detail label="Budget / video" value={`${ch.budget_per_video} ${ch.currency}`} />
                <Detail label="Monthly budget" value={`${ch.monthly_budget} ${ch.currency}`} />
                <Detail icon={ShieldCheck} label="Approval" value={ch.require_human_approval ? "Required" : "Auto"} />
              </div>
            </div>
          )}
        </div>

        <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-6" data-testid="integration-health-card">
          <h3 className="text-lg font-display font-semibold text-slate-100 mb-5">Integration health</h3>
          <div className="flex items-end gap-2 mb-2">
            <span className="text-4xl font-display font-extrabold text-slate-50">{health.connected}</span>
            <span className="text-slate-500 mb-1.5">/ {health.total} core connected</span>
          </div>
          <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden mb-2">
            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${Math.min(100, (health.connected / Math.max(1, health.total)) * 100)}%` }} />
          </div>
          {health.extra_connected > 0 && (
            <p className="text-xs text-slate-500 mb-5" data-testid="integration-extra">+{health.extra_connected} additional provider{health.extra_connected === 1 ? "" : "s"} connected (BYOK / media stack)</p>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate("/integrations")}
            className="w-full border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="overview-open-integrations">
            Manage integrations
          </Button>
        </div>
      </div>
    </div>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="text-sm text-slate-200 font-medium">{value}</div>
    </div>
  );
}
